import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import Layout from '../component/Layout';
import { Save, X, Plus } from 'lucide-react';
import statesData from './StatenDistrict.json';
import './OperatorZoneManager.css';

const OperatorZoneManager = () => {
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [selectedZone, setSelectedZone] = useState({}); // { state: [districts] }
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState(''); // Search query for states/districts

  useEffect(() => {
    const fetchData = async () => {
      try {
        // 1. Fetch basic details from operatorregistrations
        const operatorSnap = await getDocs(collection(db, 'operatorregistrations'));
        const basicDetailsMap = {};
        operatorSnap.docs.forEach(doc => {
          basicDetailsMap[doc.id] = {
            name: doc.data().organizationName || doc.id,
            mobile: doc.data().mobile || doc.data().transactionalMobile || '',
            contactState: doc.data().contactState || '',
            contactDistrict: doc.data().contactDistrict || ''
          };
        });

        // 2. Fetch segment & zone from OperatorSegmentRegistrations
        const segmentSnap = await getDocs(collection(db, 'OperatorSegmentRegistrations'));
        const operatorList = segmentSnap.docs.map(doc => {
          const data = doc.data();
          const basicDetails = basicDetailsMap[doc.id] || {};
          const zoneDistricts = Object.values(data.zone || {}).flat();

          return {
            id: doc.id,
            name: basicDetails.name,
            mobile: basicDetails.mobile,
            contactState: basicDetails.contactState,
            contactDistrict: basicDetails.contactDistrict,
            segment: data.segment || '',
            zone: data.zone || {}, // { state: [districts] }
            zoneDistricts: zoneDistricts
          };
        });

        setOperators(operatorList);
        console.log('[OperatorZoneManager] Loaded operators from 2 collections:', operatorList);
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const openAssignModal = (operator) => {
    setSelectedOperator(operator);
    // Initialize selectedZone with current zone
    setSelectedZone(JSON.parse(JSON.stringify(operator.zone)));
    setMessage('');
    setSearchQuery('');
    setShowModal(true);
  };

  // Get segment name
  const getSegmentName = (segmentObj) => {
    if (typeof segmentObj === 'string') return segmentObj;
    return Object.keys(segmentObj || {})[0] || '';
  };

  // Handle state checkbox (select/deselect all districts in state)
  const handleStateToggle = (state, checked) => {
    const stateObj = statesData.states.find(s => s.state === state);
    if (!stateObj) return;

    setSelectedZone(prev => {
      const updated = { ...prev };
      if (checked) {
        updated[state] = stateObj.districts;
      } else {
        delete updated[state];
      }
      return updated;
    });
  };

  // Handle district checkbox
  const handleDistrictToggle = (state, district, checked) => {
    setSelectedZone(prev => {
      const updated = { ...prev };
      if (checked) {
        if (!updated[state]) {
          updated[state] = [];
        }
        if (!updated[state].includes(district)) {
          updated[state].push(district);
        }
      } else {
        if (updated[state]) {
          updated[state] = updated[state].filter(d => d !== district);
          if (updated[state].length === 0) {
            delete updated[state];
          }
        }
      }
      return updated;
    });
  };

  // Check if state is fully selected
  const isStateFullySelected = (state) => {
    const stateObj = statesData.states.find(s => s.state === state);
    if (!stateObj) return false;
    return (
      selectedZone[state] &&
      selectedZone[state].length === stateObj.districts.length &&
      stateObj.districts.every(d => selectedZone[state].includes(d))
    );
  };

  // Check if state is partially selected
  const isStatePartiallySelected = (state) => {
    return selectedZone[state] && selectedZone[state].length > 0 && !isStateFullySelected(state);
  };

  // Filter states/districts based on search query
  const getFilteredStates = () => {
    if (!searchQuery.trim()) return statesData.states;

    const query = searchQuery.toLowerCase().trim();
    return statesData.states
      .map(stateObj => ({
        ...stateObj,
        districts: stateObj.districts.filter(d => d.toLowerCase().includes(query))
      }))
      .filter(stateObj => 
        stateObj.state.toLowerCase().includes(query) || 
        stateObj.districts.length > 0
      );
  };

  const handleSaveAssignment = async () => {
    if (!selectedOperator) return;

    setSaving(true);
    try {
      // Update OperatorSegmentRegistrations with new zone
      await setDoc(
        doc(db, 'OperatorSegmentRegistrations', selectedOperator.id),
        {
          zone: selectedZone,
          updatedAt: new Date()
        },
        { merge: true }
      );

      // Update local state
      const updatedOperators = operators.map(op =>
        op.id === selectedOperator.id
          ? {
              ...op,
              zone: selectedZone,
              zoneDistricts: Object.values(selectedZone).flat()
            }
          : op
      );
      setOperators(updatedOperators);

      console.log(`✅ [Zone Update] ${selectedOperator.name}: Zone updated with ${Object.values(selectedZone).flat().length} districts`);
      setMessage(`✅ Zone updated successfully!`);
      setTimeout(() => {
        setShowModal(false);
        setSelectedOperator(null);
      }, 1500);
    } catch (err) {
      console.error('Error saving:', err);
      setMessage('❌ Failed to save zone');
    } finally {
      setSaving(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedOperator(null);
    setMessage('');
  };

  if (loading) return <Layout pageTitle="Operator Assignment"><div className="operator-manager"><p>Loading...</p></div></Layout>;

  return (
    <Layout pageTitle="Operator Assignment Manager">
      <div className="operator-manager">
        <h2>Operator Zone Manager</h2>
        <p className="subtitle">Assign districts to operators by state</p>

        {/* Operators Grid */}
        <div className="operators-grid">
          {operators.map(operator => {
            const segmentName = getSegmentName(operator.segment);

            return (
              <div key={operator.id} className="operator-card">
                <div className="card-header">
                  <h3>{operator.name}</h3>
                  <span className="operator-id">{operator.id}</span>
                </div>

                <div className="card-details">
                  <div className="detail-row">
                    <span className="label">Segment:</span>
                    <span className="value">
                      <span className="badge">{segmentName?.toUpperCase() || 'N/A'}</span>
                    </span>
                  </div>

                  <div className="detail-row">
                    <span className="label">Mobile:</span>
                    <span className="value">{operator.mobile || '—'}</span>
                  </div>

                  <div className="detail-row">
                    <span className="label">Contact:</span>
                    <span className="value">{operator.contactDistrict || operator.contactState || '—'}</span>
                  </div>

                  <div className="detail-row">
                    <span className="label">Zone Districts:</span>
                    <span className="zone-count">
                      {operator.zoneDistricts.length} district{operator.zoneDistricts.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>

                {/* Zone Districts Display */}
                {operator.zoneDistricts.length > 0 && (
                  <div className="zone-districts">
                    <p className="zone-label">Current Zone:</p>
                    <div className="district-tags">
                      {operator.zoneDistricts.slice(0, 5).map((district, idx) => (
                        <span key={idx} className="district-tag">
                          {district}
                        </span>
                      ))}
                      {operator.zoneDistricts.length > 5 && (
                        <span className="more-tag">+{operator.zoneDistricts.length - 5} more</span>
                      )}
                    </div>
                  </div>
                )}

                <button
                  className="btn-assign"
                  onClick={() => openAssignModal(operator)}
                >
                  <Plus size={16} /> Assign Zone
                </button>
              </div>
            );
          })}
        </div>

        {/* Assignment Modal */}
        {showModal && selectedOperator && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h3>Assign Zone by State & District</h3>
                  <p className="modal-subtitle">
                    {selectedOperator.name} • <span className="segment-badge">{getSegmentName(selectedOperator.segment)?.toUpperCase()}</span>
                  </p>
                </div>
                <button className="btn-close" onClick={closeModal}>
                  <X size={20} />
                </button>
              </div>

              {message && (
                <div className={`message ${message.includes('✅') ? 'success' : 'error'}`}>
                  {message}
                </div>
              )}

              <div className="modal-body">
                <div className="search-box">
                  <input
                    type="text"
                    placeholder="🔍 Search states or districts..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-input"
                  />
                  {searchQuery && (
                    <button 
                      className="search-clear"
                      onClick={() => setSearchQuery('')}
                      title="Clear search"
                    >
                      ✕
                    </button>
                  )}
                </div>

                <label className="modal-label">Select States & Districts</label>
                <div className="states-list">
                  {getFilteredStates().length > 0 ? (
                    getFilteredStates().map(stateObj => {
                      const isFullySelected = isStateFullySelected(stateObj.state);
                      const isPartiallySelected = isStatePartiallySelected(stateObj.state);

                      return (
                        <div key={stateObj.state} className="state-group">
                          <label className="state-label">
                            <input
                              type="checkbox"
                              checked={isFullySelected}
                              ref={ref => {
                                if (ref && isPartiallySelected) {
                                  ref.indeterminate = true;
                                }
                              }}
                              onChange={(e) => handleStateToggle(stateObj.state, e.target.checked)}
                            />
                            <span className="state-name">{stateObj.state}</span>
                            <span className="state-count">
                              ({selectedZone[stateObj.state]?.length || 0}/{stateObj.districts.length})
                            </span>
                          </label>

                          <div className="districts-grid">
                            {stateObj.districts.map(district => {
                              const isChecked = selectedZone[stateObj.state]?.includes(district) || false;

                              return (
                                <label key={district} className="district-checkbox">
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={(e) => handleDistrictToggle(stateObj.state, district, e.target.checked)}
                                  />
                                  <span>{district}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="no-results">No states or districts found</p>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <p className="selected-count">
                  {Object.values(selectedZone).flat().length} district{Object.values(selectedZone).flat().length !== 1 ? 's' : ''} selected
                </p>
              </div>

              <div className="modal-actions">
                <button className="btn-cancel" onClick={closeModal}>
                  Cancel
                </button>
                <button
                  className="btn-save-modal"
                  onClick={handleSaveAssignment}
                  disabled={saving}
                >
                  <Save size={16} /> {saving ? 'Saving...' : 'Save Zone'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default OperatorZoneManager;
